import { BaseService } from "../baseService";
import { ShipmentAPIService, GetCommercialInvoiceUrlsResponse } from "./shipmentAPIService";
import { FileAPIService } from "../fileAPIService";
import { sanitiseParams } from "src/utils";
import { PaginatedShipments, ShipmentPaginationOptions, ShipmentRecord, PopulatedShipmentRecord } from "src/types";
import { PostShipmentRequestBody } from "src/types/api/shipments/postShipment";
import { GetShipmentStatsResponse } from "src/types/api/shipments/getShipmentStats";
import { PatchShipmentRequestBody } from "src/types/api/shipments/patchShipment";
import { EnqueueShipmentJobResponse, ShipmentJobRecord } from "src/types/api/shipments/shipmentJob";

class ShipmentService extends BaseService<ShipmentAPIService> {
  private readonly fileAPIService: FileAPIService;

  constructor(apiService: ShipmentAPIService, fileAPIService: FileAPIService) {
    super(apiService);
    this.fileAPIService = fileAPIService;
  }

  @sanitiseParams()
  async createShipment(requestBody: PostShipmentRequestBody): Promise<EnqueueShipmentJobResponse> {
    return await this.apiService.postShipment(requestBody);
  }

  @sanitiseParams()
  async getShipmentJob(jobId: string): Promise<ShipmentJobRecord> {
    return await this.apiService.getShipmentJob(jobId);
  }

  async getShipments(queryParams?: ShipmentPaginationOptions): Promise<PaginatedShipments> {
    return await this.apiService.getShipments(queryParams);
  }

  @sanitiseParams()
  async getShipmentById(shipmentId: string): Promise<PopulatedShipmentRecord> {
    return await this.apiService.getShipmentById(shipmentId);
  }

  async getShipmentStats(): Promise<GetShipmentStatsResponse> {
    return await this.apiService.getShipmentStats();
  }

  async getCommercialInvoiceUrls(shipmentId: string): Promise<GetCommercialInvoiceUrlsResponse> {
    return await this.apiService.getCommercialInvoiceUrls(shipmentId);
  }

  async getCommercialInvoices(shipmentId: string): Promise<File[]> {
    const { invoices } = await this.apiService.getCommercialInvoiceUrls(shipmentId);
    const lastModified = new Date().getTime();

    return await Promise.all(
      invoices.map(async (invoice) => {
        const fileBlob = await this.fileAPIService.getFile(invoice.url);
        return new File([fileBlob], `commercial-invoice-${invoice.invoiceNumber}.pdf`, {
          type: "application/pdf",
          lastModified
        });
      })
    );
  }

  @sanitiseParams()
  async bookParcelHero(shipmentId: string, body?: Record<string, unknown>): Promise<ShipmentRecord> {
    return await this.apiService.postBookParcelHero(shipmentId, body);
  }

  @sanitiseParams()
  async getParcelHeroDetails(shipmentId: string): Promise<ShipmentRecord> {
    return await this.apiService.getParcelHeroDetails(shipmentId);
  }

  @sanitiseParams()
  async cancelParcelHero(shipmentId: string): Promise<ShipmentRecord> {
    return await this.apiService.postCancelParcelHero(shipmentId);
  }

  @sanitiseParams()
  async rerunParcelHero(shipmentId: string, body?: Record<string, unknown>): Promise<ShipmentRecord> {
    return await this.apiService.postRerunParcelHero(shipmentId, body);
  }

  @sanitiseParams()
  async rescheduleParcelHero(shipmentId: string, collectionDate: string): Promise<ShipmentRecord> {
    return await this.apiService.putRescheduleParcelHero(shipmentId, collectionDate);
  }

  @sanitiseParams()
  async editShipment(shipmentId: string, requestBody: PatchShipmentRequestBody): Promise<EnqueueShipmentJobResponse> {
    return await this.apiService.patchShipment(shipmentId, requestBody);
  }

  @sanitiseParams()
  async finalizeShipment(shipmentId: string): Promise<ShipmentRecord> {
    return await this.apiService.postFinalizeShipment(shipmentId);
  }
}

export { ShipmentService };
